import React, { useEffect, useState } from "react";
import api, { formatApiError } from "../lib/api";
import { Download, X, Loader2, Calendar, Filter } from "lucide-react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Export dialog with filters (date range, project, status, circle, engineer, billing status).
 * Different filter sets are shown for different collections.
 */
export default function ExportDialog({ endpoint, label = "Export", filters = "tasks", onClose }) {
  const [projects, setProjects] = useState([]);
  const [engineers, setEngineers] = useState([]);
  const [masters, setMasters] = useState(null);
  const [busy, setBusy] = useState(false);

  const [form, setForm] = useState({
    from_date: "",
    to_date: "",
    project_name: "",
    status: "",
    circle: "",
    engineer_id: "",
    billing_status: "",
  });

  useEffect(() => {
    if (["tasks", "all"].includes(filters)) {
      api.get("/projects").then((r) => setProjects(r.data)).catch(() => {});
    }
    api.get("/users", { params: { role: "engineer" } }).then((r) => setEngineers(r.data)).catch(() => {});
    api.get("/masters").then((r) => setMasters(r.data)).catch(() => {});
  }, [filters]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const doExport = async () => {
    setBusy(true);
    try {
      const params = new URLSearchParams();
      Object.entries(form).forEach(([k, v]) => { if (v) params.append(k, v); });
      const t = localStorage.getItem("fieldops_token");
      const url = `${API}/api/export/${endpoint}${params.toString() ? "?" + params.toString() : ""}`;
      const res = await fetch(url, { headers: { Authorization: `Bearer ${t}` } });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        throw new Error(j.detail || "Export failed");
      }
      const blob = await res.blob();
      const dl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = dl;
      const name = endpoint.replace(".xlsx", "");
      const suffix = form.from_date && form.to_date ? `_${form.from_date}_to_${form.to_date}` : "";
      a.download = `${name}${suffix}_${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(dl);
      toast.success("Downloaded");
      onClose();
    } catch (e) {
      toast.error(e.message || "Export failed");
    } finally {
      setBusy(false);
    }
  };

  const showProject = ["tasks", "all"].includes(filters);
  const showStatus = ["tasks", "all", "expenses", "leaves"].includes(filters);
  const showCircle = ["tasks", "all"].includes(filters);
  const showBilling = ["tasks", "all"].includes(filters);
  const showEngineer = true;

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-[#13151A] border border-white/10 rounded-sm w-full max-w-lg max-h-[92vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-white/10 sticky top-0 bg-[#13151A] z-10">
          <div>
            <div className="mono text-[10px] uppercase tracking-[0.2em] text-[#FF5722]">Data Export</div>
            <h2 className="text-xl font-semibold">{label}</h2>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-white/5"><X className="w-4 h-4" /></button>
        </div>

        <div className="p-6 space-y-4">
          <div className="text-xs text-slate-400 flex items-start gap-2">
            <Filter className="w-3.5 h-3.5 shrink-0 mt-0.5 text-[#FF5722]" />
            <span>Apply filters below to narrow the export. Leave blank for all records.</span>
          </div>

          {/* Date range */}
          <div className="border border-white/10 bg-[#0B0C10] p-4 rounded-sm">
            <div className="flex items-center gap-2 mb-3">
              <Calendar className="w-3.5 h-3.5 text-[#FF5722]" />
              <span className="mono text-[10px] uppercase tracking-widest text-slate-300">Date Range</span>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="mono text-[9px] uppercase tracking-widest text-slate-500 mb-1 block">From</label>
                <input type="date" className="input" value={form.from_date} onChange={(e) => set("from_date", e.target.value)} data-testid="exp-from-date" />
              </div>
              <div>
                <label className="mono text-[9px] uppercase tracking-widest text-slate-500 mb-1 block">To</label>
                <input type="date" className="input" value={form.to_date} onChange={(e) => set("to_date", e.target.value)} data-testid="exp-to-date" />
              </div>
            </div>
            {/* Quick date presets */}
            <div className="flex flex-wrap gap-2 mt-3">
              {[
                { l: "Today", days: 0 },
                { l: "Last 7d", days: 7 },
                { l: "Last 30d", days: 30 },
                { l: "This month", type: "month" },
                { l: "Last month", type: "prev-month" },
              ].map((p) => (
                <button
                  key={p.l}
                  onClick={() => {
                    const today = new Date();
                    const to = today.toISOString().slice(0, 10);
                    let from;
                    if (p.type === "month") {
                      from = new Date(today.getFullYear(), today.getMonth(), 1).toISOString().slice(0, 10);
                    } else if (p.type === "prev-month") {
                      const first = new Date(today.getFullYear(), today.getMonth() - 1, 1);
                      const last = new Date(today.getFullYear(), today.getMonth(), 0);
                      set("from_date", first.toISOString().slice(0, 10));
                      set("to_date", last.toISOString().slice(0, 10));
                      return;
                    } else {
                      const d = new Date(); d.setDate(d.getDate() - p.days);
                      from = d.toISOString().slice(0, 10);
                    }
                    set("from_date", from); set("to_date", to);
                  }}
                  data-testid={`preset-${p.l.replace(/\s+/g, "-")}`}
                  className="mono text-[10px] uppercase tracking-widest px-2 py-1 border border-white/15 hover:border-[#FF5722] hover:text-[#FF5722] text-slate-400"
                >
                  {p.l}
                </button>
              ))}
            </div>
          </div>

          {/* Other filters */}
          <div className="grid grid-cols-1 gap-3">
            {showProject && (
              <Field label="Project">
                <select className="input" value={form.project_name} onChange={(e) => set("project_name", e.target.value)} data-testid="exp-filter-project">
                  <option value="">All projects</option>
                  {projects.map((p) => <option key={p.id} value={p.name}>{p.name}</option>)}
                </select>
              </Field>
            )}
            {showStatus && filters !== "expenses" && filters !== "leaves" && (
              <Field label="Task Status">
                <select className="input" value={form.status} onChange={(e) => set("status", e.target.value)} data-testid="exp-filter-status">
                  <option value="">All statuses</option>
                  {masters?.statuses.map((s) => <option key={s}>{s}</option>)}
                </select>
              </Field>
            )}
            {filters === "expenses" && (
              <Field label="Expense Status">
                <select className="input" value={form.status} onChange={(e) => set("status", e.target.value)} data-testid="exp-filter-status">
                  <option value="">All</option>
                  <option value="submitted">Submitted</option>
                  <option value="manager_approved">Manager Approved</option>
                  <option value="admin_approved">Admin Approved</option>
                  <option value="paid">Paid</option>
                  <option value="rejected">Rejected</option>
                </select>
              </Field>
            )}
            {filters === "leaves" && (
              <Field label="Leave Status">
                <select className="input" value={form.status} onChange={(e) => set("status", e.target.value)} data-testid="exp-filter-status">
                  <option value="">All</option>
                  <option value="submitted">Submitted</option>
                  <option value="manager_approved">Manager Approved</option>
                  <option value="admin_approved">Admin Approved</option>
                  <option value="rejected">Rejected</option>
                </select>
              </Field>
            )}
            {showCircle && (
              <Field label="Circle">
                <select className="input" value={form.circle} onChange={(e) => set("circle", e.target.value)} data-testid="exp-filter-circle">
                  <option value="">All circles</option>
                  {masters?.circles.map((c) => <option key={c}>{c}</option>)}
                </select>
              </Field>
            )}
            {showBilling && (
              <Field label="Billing Status">
                <select className="input" value={form.billing_status} onChange={(e) => set("billing_status", e.target.value)} data-testid="exp-filter-billing">
                  <option value="">All</option>
                  {masters?.billing_statuses.map((s) => <option key={s}>{s}</option>)}
                </select>
              </Field>
            )}
            {showEngineer && (
              <Field label="Engineer">
                <select className="input" value={form.engineer_id} onChange={(e) => set("engineer_id", e.target.value)} data-testid="exp-filter-engineer">
                  <option value="">All engineers</option>
                  {engineers.map((e) => <option key={e.id} value={e.id}>{e.name} {e.emp_id ? `(${e.emp_id})` : ""}</option>)}
                </select>
              </Field>
            )}
          </div>

          <div className="flex items-center justify-between text-xs pt-2 border-t border-white/10">
            <button
              onClick={() => setForm({ from_date: "", to_date: "", project_name: "", status: "", circle: "", engineer_id: "", billing_status: "" })}
              className="text-slate-500 hover:text-white mono uppercase tracking-widest"
              data-testid="exp-reset"
            >
              Clear filters
            </button>
            <span className="text-slate-500 mono">
              {Object.values(form).filter(Boolean).length} filter{Object.values(form).filter(Boolean).length !== 1 ? "s" : ""} active
            </span>
          </div>
        </div>

        <div className="flex justify-end gap-3 px-6 py-4 border-t border-white/10 sticky bottom-0 bg-[#13151A]">
          <button onClick={onClose} className="border border-white/15 px-4 py-2 text-sm rounded-sm">Cancel</button>
          <button
            onClick={doExport}
            disabled={busy}
            data-testid="exp-submit"
            className="bg-[#FF5722] hover:bg-[#F4511E] px-5 py-2 text-sm rounded-sm flex items-center gap-2 font-medium disabled:opacity-50"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
            Download Excel
          </button>
        </div>

        <style>{`.input { width: 100%; background: #0B0C10; border: 1px solid rgba(255,255,255,0.1); padding: 8px 12px; font-size: 13px; border-radius: 2px; color: white; }
          .input:focus { border-color: #FF5722; outline: none; }`}</style>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="mono text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-1 block">{label}</label>
      {children}
    </div>
  );
}
